import { useCallback, useEffect, useRef, useState } from 'react';

/** How long a finished run window stays up before closing itself. */
export const AUTO_CLOSE_SECONDS = 10;

/**
 * Close a finished run window by itself after a short countdown.
 *
 * Counts down while `active` (the run has ended), then calls `onClose` — the
 * same thing the window's X does. `keepOpen` stops the countdown for good: the
 * user is looking at the window (opening Details, picking a report format), and
 * closing it under them would lose what they reached for. A new run (`active`
 * going false then true again) starts a fresh countdown.
 *
 * @param {boolean}  active   true once the run has finished
 * @param {Function} onClose  what the window's X does
 * @param {number}   seconds
 * @returns {{ secondsLeft: number|null, keepOpen: Function }}
 *          secondsLeft is null when no countdown is showing
 */
const useAutoClose = (active, onClose, seconds = AUTO_CLOSE_SECONDS) => {
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [kept, setKept] = useState(false);

  // Latest onClose without restarting the countdown when its identity changes
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Every run gets its own countdown — "Keep open" on the last one doesn't stick
  useEffect(() => {
    if (!active) setKept(false);
  }, [active]);

  useEffect(() => {
    if (!active || kept) {
      setSecondsLeft(null);
      return undefined;
    }
    let left = seconds;
    setSecondsLeft(left);
    const id = setInterval(() => {
      left -= 1;
      if (left <= 0) {
        clearInterval(id);
        setSecondsLeft(null);
        onCloseRef.current?.();
        return;
      }
      setSecondsLeft(left);
    }, 1000);
    return () => clearInterval(id);
  }, [active, kept, seconds]);

  const keepOpen = useCallback(() => setKept(true), []);

  return { secondsLeft, keepOpen };
};

export default useAutoClose;
